/**
 * Admin User Filters Component
 *
 * Filters and search for admin user list.
 *
 * @fileoverview Admin user filters component
 */

import React from 'react';
import {
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Button,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import { MdSearch, MdFilterAltOff } from 'react-icons/md';
import type { AdminTenant, GlobalRole } from '../../types/admin';

interface AdminUserFiltersProps {
  /** Current search value */
  search: string;
  /** Current active filter value */
  isActive: boolean | undefined;
  /** Current global role filter value */
  globalRole: GlobalRole | undefined;
  /** Current tenant filter value */
  tenantId: string | undefined;
  /** Available tenants for dropdown */
  tenants: AdminTenant[];
  /** Search change callback */
  onSearchChange: (search: string) => void;
  /** Active filter change callback */
  onIsActiveChange: (isActive: boolean | undefined) => void;
  /** Global role filter change callback */
  onGlobalRoleChange: (globalRole: GlobalRole | undefined) => void;
  /** Tenant filter change callback */
  onTenantIdChange: (tenantId: string | undefined) => void;
  /** Reset filters callback */
  onReset: () => void;
}

/**
 * Admin User Filters Component
 *
 * Provides search and filter controls for the user list.
 *
 * @param props - Component props
 * @returns React component
 *
 * @example
 * <AdminUserFilters
 *   search={search}
 *   isActive={isActive}
 *   globalRole={globalRole}
 *   tenantId={tenantId}
 *   tenants={tenants}
 *   onSearchChange={setSearch}
 *   onIsActiveChange={setIsActive}
 *   onGlobalRoleChange={setGlobalRole}
 *   onTenantIdChange={setTenantId}
 *   onReset={resetFilters}
 * />
 */
export const AdminUserFilters: React.FC<AdminUserFiltersProps> = ({
  search,
  isActive,
  globalRole,
  tenantId,
  tenants,
  onSearchChange,
  onIsActiveChange,
  onGlobalRoleChange,
  onTenantIdChange,
  onReset,
}) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const selectBg = useColorModeValue('white', 'gray.700');
  const selectColor = useColorModeValue('gray.800', 'white');
  const optionBg = useColorModeValue('white', 'gray.700');

  // Common styles for Select components
  const selectStyles = {
    bg: selectBg,
    color: selectColor,
    sx: {
      '& option': {
        bg: optionBg,
        color: selectColor,
      },
    },
  };

  /**
   * Check if any filters are active
   */
  const hasActiveFilters =
    search !== '' ||
    isActive !== undefined ||
    globalRole !== undefined ||
    tenantId !== undefined;

  /**
   * Handle active filter change
   */
  const handleIsActiveChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === '') {
      onIsActiveChange(undefined);
    } else {
      onIsActiveChange(value === 'true');
    }
  };

  /**
   * Handle global role filter change
   */
  const handleGlobalRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === '') {
      onGlobalRoleChange(undefined);
    } else {
      onGlobalRoleChange(value as GlobalRole);
    }
  };

  /**
   * Handle tenant filter change
   */
  const handleTenantIdChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === '') {
      onTenantIdChange(undefined);
    } else {
      onTenantIdChange(value);
    }
  };

  return (
    <HStack
      spacing={4}
      p={4}
      bg={bgColor}
      borderRadius="lg"
      border="1px"
      borderColor={borderColor}
      wrap="wrap"
    >
      {/* Search */}
      <InputGroup maxW="300px">
        <InputLeftElement pointerEvents="none">
          <Icon as={MdSearch} color="gray.400" />
        </InputLeftElement>
        <Input
          placeholder="Search by email or name..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </InputGroup>

      {/* Tenant filter */}
      <Select
        placeholder="All Tenants"
        value={tenantId || ''}
        onChange={handleTenantIdChange}
        maxW="200px"
        {...selectStyles}
      >
        {tenants.map((tenant) => (
          <option key={tenant.id} value={tenant.id}>
            {tenant.name}
          </option>
        ))}
      </Select>

      {/* Status filter */}
      <Select
        placeholder="All Status"
        value={isActive === undefined ? '' : isActive.toString()}
        onChange={handleIsActiveChange}
        maxW="150px"
        {...selectStyles}
      >
        <option value="true">Active</option>
        <option value="false">Inactive</option>
      </Select>

      {/* Role filter */}
      <Select
        placeholder="All Roles"
        value={globalRole || ''}
        onChange={handleGlobalRoleChange}
        maxW="150px"
        {...selectStyles}
      >
        <option value="USER">User</option>
        <option value="ADMIN">Admin</option>
      </Select>

      {/* Reset button */}
      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<Icon as={MdFilterAltOff} />}
          onClick={onReset}
        >
          Clear Filters
        </Button>
      )}
    </HStack>
  );
};

export default AdminUserFilters;
